import store from '@frontend/store';
import { ShowPageScopeCollector } from '@frontend/group/modules/scope-collectors/show-page-collector';
import { DISPLAY_BUILDER_LAYER_META_KEY, getAdditionalBackgroundIndex } from '@frontend/components/display-builder/display-layer-metadata';

/** Delete one V2 artwork slot, keeping every surviving layer's settings together. */
export function deleteArtworkLayer(target) {
  const canvas = target?.canvas;
  if (!window.isDisplayBuilderV2 || !canvas || target.displayBuilderV2LayerRemoved) return false;
  const scope = ShowPageScopeCollector.get(['canvas_data', 'previews', 'getChangesLayoutById', 'updateLayoutChanges']);
  const instanceId = canvas.instance_id;
  const instance = scope.canvas_data?.[instanceId];
  const current = instance?._data;
  const preview = scope.previews?.find((item) => item.canvas === canvas);
  const index = getAdditionalBackgroundIndex(target);
  const objects = canvas.getObjects();
  
  if (!current || index === null || !objects.some((object) => object === target || (target.element_id && object.element_id === target.element_id))) return false;

  const source = { ...preview?.data, ...current, ...scope.getChangesLayoutById?.(instanceId) };
  if (!Array.isArray(source.additional_background_images) || index >= source.additional_background_images.length) return false;

  const write = (key, value) => {
    current[key] = value;
    if (preview?.data) preview.data[key] = value;
    scope.updateLayoutChanges(instanceId, key, value);
  };

  const reindex = (id) => {
    if (typeof id !== 'string') return id;
    const match = id.match(/^(add_bk_theme_img_|addbkthemeimg|additional-)(\d+)$/);
    if (!match) return id;
    const slot = Number(match[2]);
    return slot === index ? null : match[1] + (slot > index ? slot - 1 : slot);
  };

  // All add_bk_img_* arrays are indexed by artwork slot, including new options
  // such as preserve_dimensions. Never maintain a second, incomplete allowlist.
  Object.entries(source).forEach(([key, value]) => {
    if (!Array.isArray(value) || !(key.startsWith('add_bk_img_') || /^additional_background_images(?:_|$)/.test(key))) return;
    const next = value.slice();
    next.splice(index, 1);
    write(key, next);
  });

  // Locks, custom shadows and other scalar overrides use element IDs instead
  // of array slots. Clear old keys first so shifted keys cannot overwrite one another.
  const overrides = Object.entries(source).filter(([key]) => /^additional-\d+_/.test(key));
  overrides.forEach(([key]) => write(key, null));
  overrides.forEach(([key, value]) => {
    const [, elementId, suffix] = key.match(/^(additional-\d+)(_.+)$/);
    const nextId = reindex(elementId);
    if (nextId !== null) write(nextId + suffix, value);
  });

  if (source.users_fields_order) {
    const encoded = typeof source.users_fields_order === 'string';
    let order;
    try { order = encoded ? JSON.parse(source.users_fields_order) : source.users_fields_order; }
    catch { /* Preserve unrelated invalid legacy ordering rather than aborting deletion. */ }

    if (Array.isArray(order)) {
      order = order.map(reindex).filter((id) => id !== null);
      write('users_fields_order', encoded ? JSON.stringify(order) : order);
    }
  }

  if (source.settings_template) {
    const encoded = typeof source.settings_template === 'string';
    let settings;
    try { 
      settings = encoded ? JSON.parse(source.settings_template) : source.settings_template; 
    } catch { /* Leave invalid legacy metadata untouched. */ }

    if (Array.isArray(settings?.fields)) {
      settings = { ...settings, fields: settings.fields.map((field) => field && ({ ...field, id: reindex(field.id) }))
        .filter((field) => field?.id !== null) };
      write('settings_template', encoded ? JSON.stringify(settings) : settings);
    }
  }

  // Update logo references before indexes are reused. The shared editor emits
  // a later logo-deleted event carrying the OLD ID, which may now name a survivor.
  const states = new Set([source.display_builder, current.display_builder, preview?.data?.display_builder,
    window.displayBuilderState, window.displayBuilderPanel?.state, window.displayBuilderHelper?.state]);
  states.forEach((state) => {
    if (!Array.isArray(state?.logos)) return;
    state.logos = state.logos.filter((logo) => String(logo.canvas_object_id) !== String(target.id)
      && !(target.displayBuilderAssetKey && logo.assetKey === target.displayBuilderAssetKey))
      .map((logo) => ({ ...logo, canvas_object_id: reindex(logo.canvas_object_id) }));
  });

  const layers = store.getters['layout/layers']?.filter((layer) => layer.element_id !== target.element_id)
    .map((layer) => ({ ...layer, element_id: reindex(layer.element_id), ...(typeof layer.id === 'string' ? { id: reindex(layer.id) } : {}) }));
  target.displayBuilderV2LayerRemoved = true;
  ['additionalBkImgCoords', 'additional_background_theme_image'].forEach((key) => {
    if (Array.isArray(instance[key])) instance[key].splice(index, 1);
  });

  instance.additional_background_theme_image?.forEach((field, slot) => { field.real_id = `additional-${slot}`; });
  objects.forEach((object) => {
    if (object === target || (target.element_id && object.element_id === target.element_id)) {
      canvas.remove(object);
      return;
    }

    const objectIndex = getAdditionalBackgroundIndex(object);

    if (objectIndex !== null && objectIndex > index) {
      object.id = reindex(object.id);
      object.element_id = reindex(object.element_id);
    }
  });
  if (layers) {
    store.commit('layout/SET_LAYERS', layers);
  }

  window.displayBuilderHelper?.setDisplayLayerMetadata?.(current[DISPLAY_BUILDER_LAYER_META_KEY] || []);
  window.dispatchEvent(new CustomEvent('display-builder:background-layers-changed'));
  canvas.requestRenderAll?.();

  return true;
}
